import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Spinner } from "reactstrap"
import { toast } from "react-toastify"

import PageWrapper from "components/Common/PageWrapper"
import { useVehicleStockInventory } from "hooks/useVehicleStockInventory"
import { getVehicleStockInventoryPdf } from "api/vehicleStockInventoryApi"
import { useReturnPath } from "hooks/useReturnPath"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import { formatReadableDate } from "utils/formatDate"

const COMPANY = {
  name: "AUROBIL CARS",
  address:
    "279/6/B, Matikata Kalshi Road, Manikdi, Mirpur, Dhaka Cantonment, Dhaka-1206",
  phones: "01911-887-731, 01911-773-331",
}

const VEHICLE_FIELDS = [
  ["vehicle_name", "Vehicle Name"],
  ["chassis_no", "Chassis No."],
  ["color", "Color"],
  ["engine_no", "Engine No."],
  ["model_year", "Model/Year"],
  ["fuel_type", "Fuel Type"],
  ["tyre_size", "Tyre Size"],
  ["cc", "CC"],
]

const renderStatus = value => {
  if (value === "yes") {
    return <span className="iv-badge iv-badge-yes">YES</span>
  }
  if (value === "no") {
    return <span className="iv-badge iv-badge-no">NO</span>
  }
  return ""
}

const InfoRow = ({ label, value }) => (
  <tr>
    <td className="iv-label">{label}</td>
    <td className="iv-colon">:</td>
    <td className="iv-value">{value || ""}</td>
  </tr>
)

const InventoryViewPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data, isLoading, refetch, isRefetching } =
    useVehicleStockInventory(id)
  const { fromPath } = useReturnPath("/vehicle-stock-inventory-list")
  const { hasAnyPermission } = useAuth()
  const canEdit = hasAnyPermission([ACCESS.VEHICLE_STOCK_INVENTORY.EDIT])
  const [pdfLoading, setPdfLoading] = useState(false)

  const inventory = data?.data ?? data ?? {}
  const vehicle = inventory.vehicle_stock || inventory.vehicleStock || {}
  const storedSender = inventory.sender_info || inventory.sender || {}
  const storedReceiver = inventory.receiver_info || inventory.receiver || {}
  const isSwapped = !!inventory.is_swapped
  const sender = isSwapped ? storedReceiver : storedSender
  const receiver = isSwapped ? storedSender : storedReceiver
  const vehicleInfo = inventory.vehicle_info || {}
  const items = Array.isArray(inventory.items) ? inventory.items : []
  const attachments = Array.isArray(inventory.attachments)
    ? inventory.attachments
    : []

  const createdAt = inventory.inventory_date || inventory.created_at
  const dateTimeText = createdAt
    ? formatReadableDate(createdAt, "dd MMM yyyy, hh:mm a")
    : ""

  document.title = inventory.id
    ? `Inventory #${inventory.id} | Aurobil Cars`
    : "Inventory | Aurobil Cars"

  const handleDownloadPdf = async () => {
    if (!inventory.id) return
    try {
      await getVehicleStockInventoryPdf(inventory.id, setPdfLoading)
    } catch (err) {
      console.error(err)
      toast.error("Failed to generate PDF.")
      setPdfLoading(false)
    }
  }

  const half = Math.ceil(items.length / 2)
  const leftItems = items.slice(0, half)
  const rightItems = items.slice(half)
  const rowCount = Math.max(leftItems.length, rightItems.length)

  return (
    <PageWrapper
      title="Inventory"
      breadcrumbItem={inventory.id ? `Inventory #${inventory.id}` : "Inventory View"}
      cardBody={false}
    >
      <div className="iv-toolbar d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div>
          <h4 className="mb-0">
            {inventory.id ? `Inventory #${inventory.id}` : "Inventory"}
          </h4>
          {dateTimeText && (
            <div className="text-muted small">{dateTimeText}</div>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button color="light" onClick={() => navigate(fromPath)}>
            <i className="mdi mdi-arrow-left me-1" />
            Back
          </Button>
          <Button
            color="light"
            onClick={() => refetch()}
            disabled={isRefetching}
            title="Refresh"
          >
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <Button
            color="info"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
          >
            {pdfLoading ? (
              <i className="bx bx-loader bx-spin me-1" />
            ) : (
              <i className="mdi mdi-download me-1" />
            )}
            PDF
          </Button>
          {canEdit && inventory.id && (
            <Button
              color="warning"
              onClick={() =>
                navigate(`/vehicle-stock-inventory-edit/${inventory.id}`)
              }
            >
              <i className="mdi mdi-pencil-outline me-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <div className="iv-preview">
          <div className="iv-page">
            <div className="iv-company-block">
              <div className="iv-company-name">
                AUROBIL <span className="iv-accent">CARS</span>
              </div>
              <div className="iv-company-meta">{COMPANY.address}</div>
              <div className="iv-company-meta">{COMPANY.phones}</div>
            </div>

            <div className="iv-title-wrap">
              <span className="iv-title-pill">Inventory</span>
            </div>

            <div className="iv-meta-line">
              Date &amp; Time: {dateTimeText}
            </div>

            <table className="iv-info-grid">
              <tbody>
                <tr>
                  <td className="iv-col">
                    <div className="iv-section-title">
                      {isSwapped
                        ? "Receiver Information"
                        : "Sender Information"}
                    </div>
                    <table className="iv-kv-table">
                      <tbody>
                        <InfoRow label="Name" value={sender.name} />
                        <InfoRow label="Mobile" value={sender.mobile} />
                        <InfoRow
                          label="Designation"
                          value={sender.designation}
                        />
                        <InfoRow label="Company/Showroom" value={sender.company} />
                      </tbody>
                    </table>
                  </td>
                  <td className="iv-col">
                    <div className="iv-section-title">
                      {isSwapped
                        ? "Sender Information"
                        : "Receiver Information"}
                    </div>
                    <table className="iv-kv-table">
                      <tbody>
                        <InfoRow label="Name" value={receiver.name} />
                        <InfoRow label="Mobile" value={receiver.mobile} />
                        <InfoRow
                          label="Designation"
                          value={receiver.designation}
                        />
                        <InfoRow label="Company" value={receiver.company} />
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="iv-section-title">Vehicle Information</div>
            <table className="iv-kv-table iv-vehicle-info-table">
              <tbody>
                {[0, 2, 4, 6].map(i => {
                  const left = VEHICLE_FIELDS[i]
                  const right = VEHICLE_FIELDS[i + 1]
                  const isCcRow = left && left[0] === "cc"
                  return (
                    <tr key={i}>
                      {left && (
                        <>
                          <td className="iv-label">{left[1]}</td>
                          <td className="iv-colon">:</td>
                          <td className="iv-value">
                            {vehicleInfo[left[0]] || ""}
                          </td>
                        </>
                      )}
                      {right && (
                        <>
                          <td className="iv-label">{right[1]}</td>
                          <td className="iv-colon">:</td>
                          <td className="iv-value">
                            {vehicleInfo[right[0]] || ""}
                            {isCcRow && vehicleInfo.mileage
                              ? `   KM : ${vehicleInfo.mileage}`
                              : ""}
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="iv-section-title">Accessories</div>
            <table className="iv-items-table">
              <thead>
                <tr>
                  <th className="iv-sl">SL</th>
                  <th className="iv-name">Items Name</th>
                  <th className="iv-status">Status</th>
                  <th className="iv-remarks">Remarks</th>
                  <th className="iv-sl">SL</th>
                  <th className="iv-name">Items Name</th>
                  <th className="iv-status">Status</th>
                  <th className="iv-remarks">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {rowCount === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-3">
                      No items recorded.
                    </td>
                  </tr>
                ) : (
                  Array.from({ length: rowCount }).map((_, i) => {
                    const left = leftItems[i]
                    const right = rightItems[i]
                    return (
                      <tr key={i}>
                        <td className="iv-sl">{left ? i + 1 : ""}</td>
                        <td>{left?.name || ""}</td>
                        <td className="iv-status">
                          {left ? renderStatus(left.value) : ""}
                        </td>
                        <td>{left?.remarks || ""}</td>
                        <td className="iv-sl">
                          {right ? half + i + 1 : ""}
                        </td>
                        <td>{right?.name || ""}</td>
                        <td className="iv-status">
                          {right ? renderStatus(right.value) : ""}
                        </td>
                        <td>{right?.remarks || ""}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>

            {inventory.remarks && (
              <div className="iv-note">
                <strong>Remarks:</strong>
                <div
                  className="iv-note-body"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {inventory.remarks}
                </div>
              </div>
            )}

            <div className="iv-note iv-auditor-note">
              <strong>Auditor's Note:</strong>
              <div className="iv-note-body">
                {inventory.auditor_note || ""}
              </div>
            </div>

            {attachments.length > 0 && (
              <div className="iv-note">
                <strong>Attachments:</strong>
                <ul className="iv-attachments">
                  {attachments.map((att, idx) => {
                    const url = typeof att === "string" ? att : att?.url
                    const name =
                      (typeof att === "object" &&
                        (att?.name || att?.filename)) ||
                      url ||
                      `Attachment ${idx + 1}`
                    return (
                      <li key={idx}>
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer">
                            {name}
                          </a>
                        ) : (
                          name
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .iv-toolbar {
          position: sticky;
          top: 70px;
          z-index: 4;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(4px);
          padding: 8px 12px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
        }
        .iv-preview {
          display: flex;
          justify-content: center;
          background: #fff;
          padding: 24px 16px;
          border-radius: 6px;
          margin: 12px 0;
          overflow-x: auto;
        }
        .iv-page {
          background: #fff;
          color: #000;
          font-family: 'Montserrat', sans-serif;
          font-size: 11px;
          line-height: 1.4;
          width: 210mm;
          max-width: 210mm;
          min-height: 297mm;
          padding: 8mm;
          border: 1px solid #ced4da;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          box-sizing: border-box;
          flex-shrink: 0;
        }
        .iv-page table { border-collapse: collapse; width: 100%; }
        .iv-page td, .iv-page th { padding: 4px 6px; vertical-align: top; }

        .iv-company-block { text-align: center; margin-bottom: 6px; }
        .iv-company-name {
          font-size: 26px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #000;
        }
        .iv-accent { color: #ed1c24; }
        .iv-company-meta { font-size: 10px; margin-top: 2px; }

        .iv-title-pill {
          display: inline-block;
          background: #ed1c24;
          color: #fff;
          font-weight: 700;
          padding: 6px 28px;
          margin: 6px auto 12px auto;
          font-size: 13px;
        }
        .iv-title-wrap { text-align: center; margin-bottom: 10px; }

        .iv-meta-line { text-align: right; font-size: 11px; margin-bottom: 8px; }

        .iv-section-title { font-weight: 700; font-size: 13px; margin: 10px 0 4px; }

        .iv-info-grid { width: 100%; border-collapse: separate; border-spacing: 0; }
        .iv-info-grid > tbody > tr > td { padding: 0; }
        .iv-info-grid .iv-col { width: 50%; vertical-align: top; padding-right: 12px; }

        .iv-kv-table { width: 100%; }
        .iv-kv-table tr td { padding: 4px 6px; }
        .iv-kv-table tr:nth-child(odd) td { background: #f4f4f4; }
        .iv-kv-table .iv-label { width: 35%; font-weight: 500; }
        .iv-kv-table .iv-colon { width: 4%; }
        .iv-kv-table .iv-value { width: 61%; border-bottom: 1px dotted #999; }

        .iv-vehicle-info-table .iv-label { width: 18%; }
        .iv-vehicle-info-table .iv-colon { width: 3%; }
        .iv-vehicle-info-table .iv-value { width: 29%; border-bottom: 1px dotted #999; }

        .iv-items-table { border-collapse: collapse; width: 100%; margin-top: 6px; }
        .iv-items-table th,
        .iv-items-table td { border: 1px solid #999; padding: 4px 6px; font-size: 10px; }
        .iv-items-table th { background: #ececec; }
        .iv-items-table .iv-sl { width: 5%; text-align: center; }
        .iv-items-table .iv-name { width: 22%; }
        .iv-items-table .iv-status { width: 8%; text-align: center; }
        .iv-items-table .iv-remarks { width: 15%; }

        .iv-badge {
          padding: 1px 8px;
          border-radius: 3px;
          font-weight: 600;
          font-size: 10px;
          border: 1px solid transparent;
        }
        .iv-badge-yes { background: #d4edda; color: #1e7e34; border-color: #c3e6cb; }
        .iv-badge-no { background: #f8d7da; color: #b21f2d; border-color: #f5c6cb; }

        .iv-note { margin-top: 12px; }
        .iv-note-body {
          border-bottom: 1px dotted #555;
          min-height: 32px;
          margin-top: 6px;
          padding-bottom: 4px;
        }
        .iv-auditor-note { margin-top: 16px; font-weight: 600; }
        .iv-attachments { margin: 6px 0 0 18px; padding: 0; }

        @media (min-width: 992px) and (max-width: 1199px) {
          .iv-page { padding: 8mm; }
        }

        @media (min-width: 768px) and (max-width: 991px) {
          .iv-page {
            width: 100%;
            max-width: 210mm;
            min-height: auto;
            padding: 8mm;
          }
        }

        @media (max-width: 767px) {
          .iv-preview { padding: 0; background: transparent; }
          .iv-page {
            width: 100%;
            max-width: 100%;
            min-height: auto;
            padding: 8mm;
            border: none;
            box-shadow: none;
          }
          .iv-info-grid .iv-col { display: block; width: 100%; padding-right: 0; }
          .iv-items-table { font-size: 9px; }
        }

        @media print {
          .iv-toolbar { display: none !important; }
          .iv-preview { background: transparent; padding: 0; margin: 0; overflow: visible; }
          .iv-page {
            width: 100%;
            max-width: none;
            min-height: auto;
            padding: 0;
            border: none;
            box-shadow: none;
          }
        }
      `}</style>
    </PageWrapper>
  )
}

export default InventoryViewPage
